import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Divider,
  Stack
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, PieChart, Pie, Cell } from 'recharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HRFeedbackView = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterPeriod, setFilterPeriod] = useState('');
  const [periods, setPeriods] = useState([]);
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  useEffect(() => {
    fetchFeedbacks();
    fetchStatistics();
  }, [filterPeriod]);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await fetch(`${API_URL}/api/empfeedback/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setFeedbacks(data.data);
        const uniquePeriods = [...new Set(data.data.map(f => f.feedbackPeriod))];
        setPeriods(uniquePeriods);
      } else {
        setError(data.message || 'Failed to load feedbacks');
      }

      try {
        const cmpRes = await fetch(`${API_URL}/api/cmp/${company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const cmpData = await cmpRes.json();
        if (cmpData && cmpData.data) {
          setCompanyConfig({
            dateFormat: cmpData.data.dateFormat || 'DD/MM/YYYY',
            timezone: cmpData.data.timezone || 'Asia/Kolkata'
          });
        }
      } catch(e) {}
    } catch (err) {
      //console.error('Fetch error:', err);
      setError('Failed to load feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem("token");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      let url = `${API_URL}/api/empfeedback/statistics`;
      if (filterPeriod) {
        url += `&feedbackPeriod=${filterPeriod}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setStatistics(data.data);
      }
    } catch (err) {
      //console.error('Statistics error:', err);
    }
  };

  const handleViewDetails = (feedback) => {
    setSelectedFeedback(feedback);
    setDialogOpen(true);
  };

  const getScoreColor = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 4) return '#4caf50';
    if (numScore >= 3) return '#ff9800';
    return '#f44336';
  };

  const getScoreBgColor = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 4) return '#e8f5e9';
    if (numScore >= 3) return '#fff3e0';
    return '#ffebee';
  };

  const getAverageScore = (feedback) => {
    const scores = [
      feedback.engagementScore,
      feedback.workLifeBalance,
      feedback.managementSupport,
      feedback.careerGrowth,
      feedback.compensation,
      feedback.workEnvironment,
      feedback.teamCollaboration,
      feedback.learningDevelopment,
      feedback.recognition,
      feedback.overallSatisfaction
    ];
    const sum = scores.reduce((acc, score) => acc + parseInt(score), 0);
    return (sum / scores.length).toFixed(1);
  };

  const getEmployeeName = (feedback) => {
    if (feedback.employee) {
      const firstName = feedback.employee.firstName || '';
      const lastName = feedback.employee.lastName || '';
      return `${firstName} ${lastName}`.trim() || 'N/A';
    }
    return 'N/A';
  };

  // Prepare chart data
  const getBarChartData = () => {
    if (!statistics || !statistics.averages) return [];
    
    return [
      { name: 'Engagement', score: parseFloat(statistics.averages.engagementScore) },
      { name: 'Work-Life', score: parseFloat(statistics.averages.workLifeBalance) },
      { name: 'Management', score: parseFloat(statistics.averages.managementSupport) },
      { name: 'Career Growth', score: parseFloat(statistics.averages.careerGrowth) },
      { name: 'Compensation', score: parseFloat(statistics.averages.compensation) },
      { name: 'Environment', score: parseFloat(statistics.averages.workEnvironment) },
      { name: 'Collaboration', score: parseFloat(statistics.averages.teamCollaboration) },
      { name: 'Learning', score: parseFloat(statistics.averages.learningDevelopment) },
      { name: 'Recognition', score: parseFloat(statistics.averages.recognition) },
      { name: 'Overall', score: parseFloat(statistics.averages.overallSatisfaction) }
    ];
  };

  const getRadarChartData = () => {
    if (!statistics || !statistics.averages) return [];
    
    return [
      { category: 'Engagement', value: parseFloat(statistics.averages.engagementScore) },
      { category: 'Work-Life', value: parseFloat(statistics.averages.workLifeBalance) },
      { category: 'Management', value: parseFloat(statistics.averages.managementSupport) },
      { category: 'Career', value: parseFloat(statistics.averages.careerGrowth) },
      { category: 'Compensation', value: parseFloat(statistics.averages.compensation) },
      { category: 'Environment', value: parseFloat(statistics.averages.workEnvironment) },
      { category: 'Collaboration', value: parseFloat(statistics.averages.teamCollaboration) },
      { category: 'Learning', value: parseFloat(statistics.averages.learningDevelopment) },
      { category: 'Recognition', value: parseFloat(statistics.averages.recognition) },
      { category: 'Overall', value: parseFloat(statistics.averages.overallSatisfaction) }
    ];
  };

  const getPieChartData = () => {
    if (!statistics || !statistics.recommendation) return [];
    
    return [
      { name: 'Would Recommend', value: statistics.recommendation.yes, color: '#4caf50' },
      { name: 'Maybe', value: statistics.recommendation.maybe, color: '#ff9800' },
      { name: 'Would Not', value: statistics.recommendation.no, color: '#f44336' }
    ];
  };

  const filteredFeedbacks = filterPeriod
    ? feedbacks.filter(f => f.feedbackPeriod === filterPeriod)
    : feedbacks;

  if (loading && !feedbacks.length) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        p: 3,
        bgcolor: 'white',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <Box>
          <Typography variant="h4" fontWeight='bold' gutterBottom>
            Employee Feedback
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and analyze employee satisfaction metrics
          </Typography>
        </Box>
        <IconButton 
          sx={{ 
            bgcolor: '#e3f2fd',
            '&:hover': { bgcolor: '#bbdefb' }
          }}
          onClick={() => { fetchFeedbacks(); fetchStatistics(); }}
        >
          <RefreshIcon sx={{ color: '#1976d2' }} />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Statistics Cards */}
      {statistics && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#e3f2fd',
                       p: 0.5,
                      borderRadius: 2,
                      mr: 2
                    }}>
                      <TrendingUpIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Total Responses
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="500" color="#1976d2">
                    {statistics.totalResponses}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Feedback submissions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #4caf50',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#e8f5e9',
                       p: 0.5,
                      borderRadius: 2,
                      mr: 2
                    }}>
                      <ThumbUpIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Would Recommend
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="500" color="#4caf50">
                    {statistics.recommendation.yes}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Positive responses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #ff9800',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#fff3e0',
                    p: 0.5,
                      borderRadius: 2,
                      mr: 2
                    }}>
                      <HelpOutlineIcon sx={{ color: '#ff9800', fontSize: 28 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Maybe Recommend
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="500" color="#ff9800">
                    {statistics.recommendation.maybe}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Neutral responses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                bgcolor: 'white',
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #f44336',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)' }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#ffebee',
                      p: 0.5,
                      borderRadius: 2,
                      mr: 2
                    }}>
                      <ThumbDownIcon sx={{ color: '#f44336', fontSize: 28 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Won't Recommend
                    </Typography>
                  </Box>
                  <Typography variant="h4" fontWeight="500" color="#f44336">
                    {statistics.recommendation.no}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Negative responses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Bar Chart */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" gutterBottom fontWeight="600" color="#1a237e" sx={{ mb: 3 }}>
                  Average Scores by Category
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={getBarChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis domain={[0, 5]} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="score" fill="#1976d2" name="Average Score">
                      {getBarChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Pie Chart */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" gutterBottom fontWeight="600" color="#1a237e" sx={{ mb: 3 }}>
                  Recommendation Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={getPieChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getPieChartData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Radar Chart */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="h6" gutterBottom fontWeight="600" color="#1a237e" sx={{ mb: 3 }}>
                  Performance Radar Overview
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <RadarChart data={getRadarChartData()}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="category" />
                    <PolarRadiusAxis domain={[0, 5]} />
                    <Radar name="Average Score" dataKey="value" stroke="#1976d2" fill="#1976d2" fillOpacity={0.6} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Filter */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <FormControl fullWidth size="small">
          <InputLabel>Filter by Period</InputLabel>
          <Select
            value={filterPeriod}
            label="Filter by Period"
            onChange={(e) => setFilterPeriod(e.target.value)}
            sx={{ bgcolor: 'white' }}
          >
            <MenuItem value="">All Periods</MenuItem>
            {periods.map(period => (
              <MenuItem key={period} value={period}>{period}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Feedbacks Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Employee ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Employee Name</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Avg Score</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Overall</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Recommend</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1a237e' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFeedbacks.length > 0 ? (
              filteredFeedbacks.map((feedback) => (
                <TableRow 
                  key={feedback.feedback_id}
                  hover
                  sx={{ 
                    '&:hover': { bgcolor: '#f5f5f5' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{feedback.employee_id}</TableCell>
                  <TableCell>{getEmployeeName(feedback)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={feedback.feedbackPeriod}
                      size="small"
                      sx={{ 
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getAverageScore(feedback)}
                      size="small"
                      sx={{ 
                        bgcolor: getScoreBgColor(getAverageScore(feedback)),
                        color: getScoreColor(getAverageScore(feedback)),
                        fontWeight: 700
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={feedback.overallSatisfaction}
                      size="small"
                      sx={{ 
                        bgcolor: getScoreBgColor(feedback.overallSatisfaction),
                        color: getScoreColor(feedback.overallSatisfaction),
                        fontWeight: 700
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={feedback.wouldRecommend}
                      size="small"
                      sx={{ 
                        bgcolor: feedback.wouldRecommend === 'Yes' ? '#e8f5e9' :
                                 feedback.wouldRecommend === 'No' ? '#ffebee' : '#fff3e0',
                        color: feedback.wouldRecommend === 'Yes' ? '#4caf50' :
                               feedback.wouldRecommend === 'No' ? '#f44336' : '#ff9800',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {dayjs(feedback.submittedAt).tz(companyConfig.timezone).format(companyConfig.dateFormat)}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small"
                      sx={{ 
                        bgcolor: '#e3f2fd',
                        '&:hover': { bgcolor: '#bbdefb' }
                      }}
                      onClick={() => handleViewDetails(feedback)}
                    >
                      <VisibilityIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    No feedback submissions found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight="700" color="#1a237e">
            Feedback Details
          </Typography>
          {selectedFeedback && (
            <Typography variant="body2" color="text.secondary">
              {getEmployeeName(selectedFeedback)} • {selectedFeedback.feedbackPeriod}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedFeedback && (
            <Stack spacing={3}>
              {/* Ratings Section */}
              <Box>
                <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                  Performance Ratings
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  {[
                    ['Engagement Score', selectedFeedback.engagementScore],
                    ['Work-Life Balance', selectedFeedback.workLifeBalance],
                    ['Management Support', selectedFeedback.managementSupport],
                    ['Career Growth', selectedFeedback.careerGrowth],
                    ['Compensation', selectedFeedback.compensation],
                    ['Work Environment', selectedFeedback.workEnvironment],
                    ['Team Collaboration', selectedFeedback.teamCollaboration],
                    ['Learning & Development', selectedFeedback.learningDevelopment],
                    ['Recognition', selectedFeedback.recognition],
                    ['Overall Satisfaction', selectedFeedback.overallSatisfaction]
                  ].map(([label, score]) => (
                    <Grid item xs={6} key={label}>
                      <Box sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        bgcolor: getScoreBgColor(score),
                        borderRadius: 1
                      }}>
                        <Typography variant="body2" fontWeight="500">
                          {label}
                        </Typography>
                        <Typography 
                          variant="h6"
                          fontWeight="700"
                          sx={{ color: getScoreColor(score) }}
                        >
                          {score}/5
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {/* Qualitative Feedback */}
              <Box>
                <Typography variant="h6" fontWeight="600" color="#1a237e" gutterBottom>
                  Detailed Feedback
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                  <Typography variant="subtitle2" fontWeight="600" color="#4caf50" gutterBottom>
                    What they like most:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {selectedFeedback.likeMost || 'No response'}
                  </Typography>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                  <Typography variant="subtitle2" fontWeight="600" color="#ff9800" gutterBottom>
                Suggested improvements:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {selectedFeedback.improvements || 'No response'}
                  </Typography>
                </Paper>

                {selectedFeedback.additionalComments && (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', borderLeft: '4px solid #1976d2' }}>
                    <Typography variant="subtitle2" fontWeight="600" color="#1976d2" gutterBottom>
                      📝 Additional comments:
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {selectedFeedback.additionalComments}
                    </Typography>
                  </Paper>
                )}
              </Box>

              {/* Recommendation */}
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                bgcolor: selectedFeedback.wouldRecommend === 'Yes' ? '#e8f5e9' :
                         selectedFeedback.wouldRecommend === 'No' ? '#ffebee' : '#fff3e0',
                borderRadius: 2
              }}>
                <Typography variant="body1" fontWeight="600">
                  Would recommend company to others:
                </Typography>
                <Chip 
                  label={selectedFeedback.wouldRecommend}
                  sx={{ 
                    bgcolor: selectedFeedback.wouldRecommend === 'Yes' ? '#4caf50' :
                             selectedFeedback.wouldRecommend === 'No' ? '#f44336' : '#ff9800',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    px: 2
                  }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            variant="contained"
            sx={{ 
              bgcolor: '#1976d2',
              '&:hover': { bgcolor: '#1565c0' },
              borderRadius: 2,
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRFeedbackView;